import React, { useState, useEffect } from 'react';
import * as THREE from "three";
import { Text as TroikaText } from "troika-three-text";
import { lambdaURL } from "../config";
import { extractSuffixFromField, sendRequest } from "./vmUtils";
import { VMProfileFilters } from './vmProfileFilters';
import { useAuth } from './vmAuthenticate';

const graphTypes = [
  { value: 'bar', label: 'Bar Graph' },
  { value: 'pie', label: 'Pie Chart' }
];

const summaryTypes = [
  { value: 'sum', label: 'Vote Sums' },
  { value: 'count', label: 'Vote Counts' }
];

export default function VMReport({ }) {
  const { user } = useAuth();
  const userId = user.userId;
  const [assemblies, setAssemblies] = useState([]);
  const [selectedAssembly, setSelectedAssembly] = useState('');
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState('');
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [graphType, setGraphType] = useState('bar');
  const [summaryType, setSummaryType] = useState('sum');
  const [scene, setScene] = useState(null);
  const [camera, setCamera] = useState(null);
  const [renderer, setRenderer] = useState(null);
  const [voteData, setVoteData] = useState({
    rawVotes: [],
    processedData: []
  });
  // Add state for profile filters
  const [profileFilters, setProfileFilters] = useState([]);

  // Materials setup
  const materials = {
    positive2: new THREE.MeshLambertMaterial({ color: 0x00aa00  }),
    positive1: new THREE.MeshLambertMaterial({ color: 0x00ff00  }),
    neutral: new THREE.MeshLambertMaterial({ color: 0x95a5a6 }),
    negative1: new THREE.MeshLambertMaterial({ color: 0xff0000  }),
    negative2: new THREE.MeshLambertMaterial({ color: 0xaa0000  })
  };

  useEffect(() => {
    // Fetch user's assemblies on component mount
    const fetchAssemblies = async () => {
      if (!userId) return;

      let jsonData = {
        command: "getUserAssemblies"
      };

      const data = await sendRequest(lambdaURL, jsonData);
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setAssemblies(parsedBody);
        }
      }
    };

    fetchAssemblies();
  }, [userId]);

  // Modify the topics fetch useEffect
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedAssembly) {
        setTopics([]);
        return;
      }

      let jsonData = {
        command: "getTopicsForAssembly",
        assemblyId: selectedAssembly
      };

      const data = await sendRequest(lambdaURL, jsonData);
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setTopics(parsedBody);
          setSelectedTopic('');
        }
      }
    };

    fetchTopics();
  }, [selectedAssembly]);

  // Update the proposals fetch useEffect to consider selected topic
  useEffect(() => {
    const fetchProposals = async () => {
      if (!selectedAssembly) {
        setProposals([]);
        return;
      }

      let jsonData = {
        command: "getAssemblyProposals",
        assemblyId: selectedAssembly,
        topicId: selectedTopic  || undefined,
        limit: 50,
        descending: "true"
      };
      console.log("Calling getAssemblyProposals with:", jsonData);
      const data = await sendRequest(lambdaURL, jsonData);
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          setProposals(parsedBody);
          setSelectedProposal('');
        }
      }
    };

    fetchProposals();
  }, [selectedAssembly, selectedTopic]); // Add selectedTopic as a dependency

  useEffect(() => {
    // Fetch vote data when an proposal is selected
    /*
    const fetchVoteData = async () => {
      if (!selectedProposal) {
        setVoteData({ rawVotes: [], processedData: [] });
        return;
      }

      // Construct the filter query
    const activeFilters = profileFilters
      .filter(f => f.questionId && f.selectedAnswers.length > 0)
      .map(filter => ({
        questionId: filter.questionId,
        answers: filter.selectedAnswers
      }));

      let jsonData = {
        command: "getVotesForTarget",
        targetId: selectedProposal,
        targetType: "PROPOSAL",
      profileFilters: activeFilters.length > 0 ? activeFilters : undefined
      };

    console.log("Fetching votes with filters:", jsonData);
      const data = await sendRequest(lambdaURL, jsonData);
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        if (parsedBody && Array.isArray(parsedBody)) {
          const processedData = processVoteData(parsedBody);
          setVoteData({
            rawVotes: parsedBody,
            processedData: processedData
          });
          updateGraph(processedData);
        }
      }
    };*/

    fetchVoteData();
  }, [selectedProposal, profileFilters]);

  // Add handler for filter changes
  const handleFiltersChange = (newFilters) => {
    setProfileFilters(newFilters);
    // Optionally trigger a new vote fetch when filters change
    fetchVoteData();
  };

  useEffect(() => {
    // Initialize THREE.js scene
    const initScene = () => {
      const canvas = document.getElementById("vm-graph-canvas");
      if (!canvas) return;

      const container = canvas.parentElement;
      const width = Math.min(container.clientWidth, 800);
      const height = 400;

      const newScene = new THREE.Scene();
      newScene.background = new THREE.Color(0xdddddd);

      const newCamera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      newCamera.position.set(0, 2, 5);
      newCamera.lookAt(0, 2, 0);

      const newRenderer = new THREE.WebGLRenderer({ canvas });
      newRenderer.setSize(width, height, false);  // Added false parameter for pixelRatio

      // Add lights
      const pointLight = new THREE.PointLight(0xffffff, 2, 0);
      pointLight.position.set(20, 0, 10);
      newScene.add(pointLight);

      const otherLight = new THREE.PointLight(0xffffff, 1, 0);
      otherLight.position.set(-10, 10, 0);
      newScene.add(otherLight);

      const ambientLight = new THREE.AmbientLight(0xffffff, 1);
      newScene.add(ambientLight);

      setScene(newScene);
      setCamera(newCamera);
      setRenderer(newRenderer);

      newRenderer.render(newScene, newCamera);
    };

    initScene();
  }, []);

  useEffect(() => {
    if (voteData.processedData.length > 0) {
      updateGraph(voteData.processedData);
    }
  }, [graphType]);

  useEffect(() => {
    if (voteData.rawVotes.length > 0) {
      const newProcessedData = processVoteData(voteData.rawVotes);
      setVoteData(prev => ({
        ...prev,
        processedData: newProcessedData
      }));
      updateGraph(newProcessedData);
    }
  }, [summaryType]);

  useEffect(() => {
    const handleResize = () => {
      if (!renderer || !camera) return;

      const canvas = document.getElementById("vm-graph-canvas");
      if (!canvas) return;

      const container = canvas.parentElement;
      const width = Math.min(container.clientWidth, 800);
      const height = 400;

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Update canvas and renderer size
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      renderer.setSize(width, height, false);

      // Update camera
      camera.aspect = width / height;
      camera.updateProjectionMatrix();

      // Re-render
      renderer.render(scene, camera);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial size setup

    return () => window.removeEventListener('resize', handleResize);
  }, [renderer, camera, scene]);

  const fetchVoteData = async () => {
    if (!selectedProposal) {
      setVoteData({ rawVotes: [], processedData: [] });
      return;
    }

    const activeFilters = profileFilters
      .filter(f => f.questionId && f.selectedAnswers.length > 0)
      .map(filter => ({
        questionId: filter.questionId,
        answers: filter.selectedAnswers
      }));

    let jsonData = {
      command: "getVotesForTarget",
      targetId: selectedProposal,
      targetType: "PROPOSAL",
      profileFilters: activeFilters.length > 0 ? activeFilters : undefined
    };

    console.log("Fetching votes with filters:", jsonData);
    const data = await sendRequest(lambdaURL, jsonData);
    if (data.body) {
      const parsedBody = JSON.parse(data.body);
      if (parsedBody && Array.isArray(parsedBody)) {
        const processedData = processVoteData(parsedBody);
        console.log("Processed data:", processedData);
        setVoteData({
          rawVotes: parsedBody,
          processedData: processedData
        });
        updateGraph(processedData);
      }
    }
  };

  const processVoteData = (votes) => {
    // Ensure votes is an array
    const votesArray = Array.isArray(votes) ? votes : [];

    if (summaryType === 'sum') {
      const totals = {
        positive: 0,
        negative: 0
      };

      votesArray.forEach(vote => {
        if (vote.value > 0) totals.positive += vote.value;
        else if (vote.value < 0) totals.negative -= vote.value;
      });

      return [
        { name: 'Against', value: totals.negative },
        { name: 'In Favor', value: totals.positive }
      ];
    } else {
      // Count-based summary
      const counts = {
        negative2: 0,
        negative1: 0,
        neutral: 0,
        positive1: 0,
        positive2: 0
      };

      votesArray.forEach(vote => {
        switch(Number(vote.value)) {
          case -2: counts.negative2++; break;
          case -1: counts.negative1++; break;
          case 0: counts.neutral++; break;
          case 1: counts.positive1++; break;
          case 2: counts.positive2++; break;
        }
      });

      return [
        { name: '-2', value: counts.negative2 },
        { name: '-1', value: counts.negative1 },
        { name: '0', value: counts.neutral },
        { name: '+1', value: counts.positive1 },
        { name: '+2', value: counts.positive2 }
      ];
    }
  };

  const clearScene = () => {
    if (!scene) return;
    while (scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }

    // Re-add lights
    const pointLight = new THREE.PointLight(0xffffff, 2, 0);
    pointLight.position.set(20, 0, 10);
    scene.add(pointLight);

    const otherLight = new THREE.PointLight(0xffffff, 1, 0);
    otherLight.position.set(-10, 10, 0);
    scene.add(otherLight);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);
  };

  const updateGraph = (data) => {
    if (!scene || !camera || !renderer || !data) return;

    clearScene();

    if (graphType === 'bar') {
      renderBarGraph(data);
    } else if (graphType === 'pie') {
      renderPieGraph(data);
    }

    renderer.render(scene, camera);
  };

  const renderBarGraph = (data) => {
    const getMaterial = (index, data) => {
      if (summaryType === 'sum') {
        return [materials.negative2, materials.positive2][index];
      }
      return [
        materials.negative2,
        materials.negative1,
        materials.neutral,
        materials.positive1,
        materials.positive2
      ][index];
    };

    // Calculate maximum value for scaling
    const maxValue = Math.max(...data.map(item => Math.abs(item.value) || 0));

    // Define maximum desired bar height in the scene
    const maxBarHeight = 3;

    // Calculate scale factor
    const scaleFactor = maxValue > 0 ? maxBarHeight / maxValue : 1;

    const margin = 1.5;
    const totalWidth = 8;
    const barWidth = (totalWidth - (margin * 2)) / data.length;
    const spacing = barWidth * 0.2;
    const actualBarWidth = barWidth - spacing;
    const startX = -totalWidth/2 + margin + (barWidth/2);

    // Clear existing bars and labels
    clearScene();

    // Add lights back
    const pointLight = new THREE.PointLight(0xffffff, 2, 0);
    pointLight.position.set(20, 0, 10);
    scene.add(pointLight);

    const otherLight = new THREE.PointLight(0xffffff, 1, 0);
    otherLight.position.set(-10, 10, 0);
    scene.add(otherLight);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    data.forEach((item, i) => {
      // Scale the height
      const scaledHeight = (item.value || 0) * scaleFactor;

      // Create bar
      const geom = new THREE.BoxGeometry(actualBarWidth, Math.abs(scaledHeight) || 0.1, 0.25);
      const mesh = new THREE.Mesh(geom, getMaterial(i, data));
      const xPos = startX + (i * barWidth);
      const yPos = scaledHeight / 2;
      mesh.position.set(xPos, yPos, 0);
      scene.add(mesh);

      // Add label
      const text = new TroikaText();
      text.text = `${item.name}\n(${item.value})`;
      text.fontSize = 0.3;
      text.color = 0x444444;
      text.anchorX = 'center';
      text.anchorY = 'middle';
      text.position.set(xPos, -0.4, 0);
      text.sync();

      text.addEventListener('sync', () => {
        renderer.render(scene, camera);
      });

      scene.add(text);
    });

    // Adjust camera position based on max height
    camera.position.set(0, maxBarHeight / 2, 5 + (maxBarHeight / 2));
    camera.lookAt(0, maxBarHeight / 2, 0);

    setTimeout(() => renderer.render(scene, camera), 100);
    setTimeout(() => renderer.render(scene, camera), 500);
  };

  /*
  const renderBarGraph = (data) => {
    const getMaterial = (index, data) => {
      if (summaryType === 'sum') {
        return [materials.negative2, materials.positive2][index];
      }
      return [
        materials.negative2,
        materials.negative1,
        materials.neutral,
        materials.positive1,
        materials.positive2
      ][index];
    };

    const margin = 1.5;
    const totalWidth = 8;
    const barWidth = (totalWidth - (margin * 2)) / data.length;
    const spacing = barWidth * 0.2;
    const actualBarWidth = barWidth - spacing;
    const startX = -totalWidth/2 + margin + (barWidth/2);

    data.forEach((item, i) => {
      const geom = new THREE.BoxGeometry(actualBarWidth, item.value || 0.1, 0.25);
      const mesh = new THREE.Mesh(geom, getMaterial(i, data));
      const xPos = startX + (i * barWidth);
      mesh.position.set(xPos, (item.value || 0.1) / 2, 0);
      scene.add(mesh);

      const text = new TroikaText();
      text.text = `${item.name}\n(${item.value})`;
      text.fontSize = 0.3;
      text.color = 0x444444;
      text.anchorX = 'center';
      text.anchorY = 'middle';
      text.position.set(xPos, -0.4, 0);
      text.sync();

      text.addEventListener('sync', () => {
        renderer.render(scene, camera);
      });

      scene.add(text);
    });

    setTimeout(() => renderer.render(scene, camera), 100);
    setTimeout(() => renderer.render(scene, camera), 500);
  };*/

  const renderPieGraph = (data) => {
    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
    if (total === 0) return;

    const radius = 2;  
    const legendStartX = -1.8;  // Common starting X for both rows
    const legendStartY = 0.5;  // First row Y position
    const legendRowSpacing = 0.5;  // Vertical spacing between rows

    const getMaterial = (index, data) => {
      if (summaryType === 'sum') {
        return [materials.negative2, materials.positive2][index];
      }
      return [
        materials.negative2,
        materials.negative1,
        materials.neutral,
        materials.positive1,
        materials.positive2
      ][index];
    };

    let start = 0;
    data.forEach((item, i) => {
        // Pie segment
        const fraction = (item.value || 0) / total;
        const geom = new THREE.CylinderGeometry(
            radius, radius, 0.5, 64, 1, false,
            start, (fraction * Math.PI * 2)
        );
        start += (fraction * Math.PI * 2);

        const mesh = new THREE.Mesh(geom, getMaterial(i, data));
        mesh.rotation.set(Math.PI / 2, 0, 0);
        mesh.position.set(0, 3, 0);
        scene.add(mesh);

        const percentage = ((fraction * 100).toFixed(1));

        // Legend text - stacked vertically
        const text = new TroikaText();
        text.text = `${item.name}: ${item.value} (${percentage}%)`;
        text.fontSize = 0.3;
        text.color = 0x444444;
        text.anchorX = 'left';
        text.anchorY = 'middle';
        // Position each row starting at the same X but different Y
        text.position.set(legendStartX + 0.3, legendStartY - (i * legendRowSpacing), 0);
        text.sync();

        text.addEventListener('sync', () => {
            renderer.render(scene, camera);
        });

        scene.add(text);

        // Legend color boxes - aligned vertically
        const legendBox = new THREE.BoxGeometry(0.3, 0.3, 0.05);
        const legendCube = new THREE.Mesh(legendBox, getMaterial(i, data));
        legendCube.position.set(legendStartX, legendStartY - (i * legendRowSpacing), 0);
        scene.add(legendCube);
    });

    setTimeout(() => renderer.render(scene, camera), 100);
    setTimeout(() => renderer.render(scene, camera), 500);
  };

  //Fix: for filters, we are going to use the General Public assembly 387a1596-ce28-4079-9bd9-aa87301aae28
  //Later we want to add to these questions the ones that are specific to the selected assembly.
  return (
    <div className="vm-section">
      <div className="vm-section-title">
        <h2>Reports</h2>
      </div>

      <div className="vm-form-vertical">
        {true && (
          <div>
            <VMProfileFilters
              assemblyId={"387a1596-ce28-4079-9bd9-aa87301aae28"}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        <div className="vm-form-row">
          <label className="vm-form-label">Assembly</label>
          <select
            className="vm-form-input-wide"
            value={selectedAssembly}
            onChange={(e) => {
              setSelectedAssembly(e.target.value);
              // Clear topic and proposal when assembly changes
              setSelectedTopic('');
              setSelectedProposal('');
            }}
          >
            <option value="">Select an Assembly</option>
            {assemblies.map(assembly => (
              <option key={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}
                      value={extractSuffixFromField(assembly.SK, 'ASSEMBLY')}>
                {assembly.name}
              </option>
            ))}
          </select>
        </div>

        <div className="vm-form-row">
          <label className="vm-form-label">Topic</label>
          <select
            className="vm-form-input-wide"
            value={selectedTopic}
            onChange={(e) => {
              setSelectedTopic(e.target.value);
              setSelectedProposal('');
            }}
            disabled={!selectedAssembly}
          >
            <option value="">All Topics</option>
            {topics.map(topic => (
              <option key={extractSuffixFromField(topic.SK, 'TOPIC')}
                      value={extractSuffixFromField(topic.SK, 'TOPIC')}>
                {topic.name}
              </option>
            ))}
          </select>
        </div>

        <div className="vm-form-row">
          <label className="vm-form-label">Proposal</label>
          <select
            className="vm-form-input-wide"
            value={selectedProposal}
            onChange={(e) => setSelectedProposal(e.target.value)}
            disabled={!selectedAssembly}
          >
            <option value="">Select a Proposal</option>
            {proposals.map(proposal => (
              <option key={extractSuffixFromField(proposal.SK, 'PROPOSAL')}
                      value={extractSuffixFromField(proposal.SK, 'PROPOSAL')}>
                {proposal.title}
              </option>
            ))}
          </select>
        </div>

        <div className="vm-form-row">
          <select
            className="vm-form-input vm-form-input-half"
            value={graphType}
            onChange={(e) => setGraphType(e.target.value)}
          >
            {graphTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <select
            className="vm-form-input vm-form-input-half"
            value={summaryType}
            onChange={(e) => setSummaryType(e.target.value)}
          >
            {summaryTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <canvas className="vm-graph-canvas" id="vm-graph-canvas"></canvas>
      </div>
    </div>
  );
}